import React from 'react';
import {
  Plane,
  RadioTower,
} from 'lucide-react';
import { formatWingitDate, formatWingitTime } from '../dateFormat';
import wingitDesign from '../designSystem';

export default function DashboardNextFlightBrief({ isMobile, nextFlight }) {
  const styles = getStyles(isMobile);
  const flightRange = getFlightDateRange(nextFlight);

  const hasFlight = Boolean(nextFlight) && !isEndedFlight(nextFlight);
  const flightState = getFlightState(nextFlight);

  const aircraft = nextFlight?.aircraft?.registration || 'No aircraft booked';
  const aircraftType = nextFlight?.aircraft?.icao_type || '';
  const aircraftImage = nextFlight?.aircraft?.image_url || '';
  const startDate = getFlightStartDate(nextFlight);
  const date = startDate ? formatWingitDate(startDate) : 'No flight scheduled';

  const startTime = getFlightStartTime(nextFlight);
  const endTime = getFlightEndTime(nextFlight);
  const startDateTime = flightRange.startDateTime;
  const endDateTime = flightRange.endDateTime;
  const endDate = getFlightEndDate(nextFlight) || getFlightStartDate(nextFlight);
  const resolvedEndDate = getDatePart(endDateTime) || endDate;
  const resolvedStartTime = startTime || getTimePart(startDateTime);
  const resolvedEndTime = getTimePart(endDateTime) || endTime;
  const isMultiDayFlight = flightRange.isMultiDay;

  const timeLabel = startTime
    ? `${formatWingitTime(startTime)}${endTime ? ` – ${formatWingitTime(endTime)}` : ''}`
    : 'Time not set';

  const rangeLabel =
    isMultiDayFlight && startDate && resolvedStartTime && resolvedEndDate && resolvedEndTime
      ? `${formatWingitDate(startDate)} • ${formatWingitTime(resolvedStartTime)} → ${formatWingitDate(resolvedEndDate)} • ${formatWingitTime(resolvedEndTime)}`
      : '';

  const briefing = nextFlight?.briefing_data || {};
  const departure = briefing.departureAirfield || 'Departure not set';
  const destination = briefing.destinationAirfield || 'Destination not set';
  const isLandAwayFlight =
    isMultiDayFlight &&
    briefing?.departureAirfield &&
    briefing?.destinationAirfield &&
    String(briefing.departureAirfield).trim().toLowerCase() !==
      String(briefing.destinationAirfield).trim().toLowerCase();
  const purpose = nextFlight?.purpose || 'Private flight';

  if (!hasFlight) {
    return (
      <section style={styles.card}>
        <div style={styles.photoPanel}>
          <div style={styles.imageOverlay} />
          <Plane size={isMobile ? 34 : 46} strokeWidth={1.8} style={styles.imageIcon} />

          <div style={styles.photoCaption}>
            <div style={styles.imageLabel}>FLIGHT</div>
            <div style={styles.photoRegistration}>Ready?</div>
            <div style={styles.imageType}>No aircraft booked</div>
          </div>
        </div>

        <div style={styles.main}>
          <div style={styles.eyebrow}>FLIGHT FOLDER</div>
          <h2 style={styles.title}>No flight booked</h2>
          <div style={styles.emptyLine}>Your next booked flight will appear here.</div>
        </div>

        <div style={styles.statusPanel}>
          <div style={styles.statusBadge}>STANDBY</div>
          <div style={styles.statusText}>Use Flight Flow to book your next flight.</div>
        </div>
      </section>
    );
  }

  return (
    <section style={styles.card}>
      <div style={styles.photoPanel}>
        {aircraftImage ? (
          <>
            <img src={aircraftImage} alt={aircraft} style={styles.aircraftImage} />
            <div style={styles.imageShade} />
          </>
        ) : (
          <>
            <div style={styles.imageOverlay} />
            <Plane size={isMobile ? 34 : 46} strokeWidth={1.8} style={styles.imageIcon} />
          </>
        )}

        <div style={styles.photoCaption}>
          <div style={styles.imageLabel}>FLIGHT</div>
          <div style={styles.photoRegistration}>{aircraft}</div>
          {aircraftType ? <div style={styles.imageType}>{aircraftType}</div> : null}
          <div style={styles.imageDate}>{date}</div>
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.eyebrow}>FLIGHT FOLDER</div>
        <h2 style={styles.title}>{flightState.title}</h2>

        {isMultiDayFlight && rangeLabel ? (
          <div style={styles.metaLine}>
            <span>{rangeLabel}</span>
          </div>
        ) : (
          <div style={styles.metaLine}>
            <span>{date}</span>
            <span style={styles.metaDot}>•</span>
            <span>{timeLabel}</span>
          </div>
        )}

        {isLandAwayFlight ? (
          <div style={styles.routeBlock}>
            <div style={styles.routeSection}>
              <div style={styles.routeLabel}>OUTBOUND</div>
              <div style={styles.routeDirection}>To {destination}</div>
            </div>

            <div style={styles.routeSection}>
              <div style={styles.routeLabel}>RETURN</div>
              <div style={styles.routeDirection}>From {departure}</div>
            </div>
          </div>
        ) : (
          <div style={styles.routeBlock}>
            <div style={styles.routeLabel}>ROUTE</div>
            <div style={styles.routeAirfield}>{departure}</div>
            <div style={styles.routeArrow}>↓</div>
            <div style={styles.routeAirfield}>{destination}</div>
          </div>
        )}

        <div style={styles.purposeChip}>
          {String(purpose).toUpperCase()}
        </div>

        {nextFlight?.previousPostFlightOutstanding ? (
          <div style={styles.continuityWarning}>
            <div style={styles.continuityWarningTitle}>
              Previous Post-Flight outstanding
            </div>

            <div style={styles.continuityWarningText}>
              This does not stop your flight. Confirm the aircraft condition
              and opening meter reading before departure.
            </div>
          </div>
        ) : null}
      </div>

      <div style={styles.statusPanel}>
        <div style={styles.statusIconWrap}>
          {flightState.key === 'current' || flightState.key === 'away' || flightState.key === 'overdue' ? (
            <RadioTower size={24} />
          ) : (
            <Plane size={24} />
          )}
        </div>

        <div style={styles.statusBadge}>{flightState.badge}</div>
        <div style={styles.statusText}>{flightState.message}</div>
      </div>
    </section>
  );
}

function getFlightState(nextFlight) {
  if (!nextFlight || isEndedFlight(nextFlight)) {
    return {
      key: 'none',
      title: 'No flight booked',
      badge: 'STANDBY',
      message: 'No active flight folder.',
    };
  }

  const { startDateTime, endDateTime, isMultiDay } = getFlightDateRange(nextFlight);
  const postFlightCompleted =
    String(nextFlight?.tech_log_status || '')
      .trim()
      .toLowerCase() === 'completed';
  const now = new Date();
  const returnBadgeDate = getReturnBadgeDate(nextFlight, endDateTime);

  if (startDateTime && now < startDateTime) {
    if (isMultiDay) {
      return {
        key: 'next',
        title: 'Next Flight',
        badge: returnBadgeDate ? `RETURNS ${returnBadgeDate}` : 'NEXT',
        message: returnBadgeDate
          ? `Scheduled booking. Aircraft returns on ${returnBadgeDate}.`
          : 'Scheduled booking with a planned return date.',
      };
    }

    return {
      key: 'next',
      title: 'Next Flight',
      badge: 'NEXT',
      message: 'Scheduled and ready for pre-flight.',
    };
  }

  if (startDateTime && endDateTime && now >= startDateTime && now <= endDateTime) {
    if (isMultiDay) {
      return {
        key: 'away',
        title: 'Aircraft Away',
        badge: returnBadgeDate ? `AWAY UNTIL ${returnBadgeDate}` : 'AIRCRAFT AWAY',
        message: 'Aircraft is booked away until its scheduled return.',
      };
    }

    return {
      key: 'current',
      title: 'Current Flight',
      badge: 'CURRENT',
      message: 'This flight is currently in progress.',
    };
  }

  if (
    startDateTime &&
    endDateTime &&
    now > endDateTime &&
    !postFlightCompleted
  ) {
    return {
      key: 'overdue',
      title: 'Current Flight',
      badge: 'FLIGHT OVERDUE',
      message: 'Post-flight completion required.',
    };
  }

  return {
    key: 'next',
    title: 'Next Flight',
    badge: returnBadgeDate && isMultiDay ? `RETURNS ${returnBadgeDate}` : 'NEXT',
    message:
      returnBadgeDate && isMultiDay
        ? `Scheduled booking. Aircraft returns on ${returnBadgeDate}.`
        : 'Scheduled and ready for pre-flight.',
  };
}

function getFlightDateRange(nextFlight) {
  const startDate = getFlightStartDate(nextFlight);
  const explicitEndDate = getFlightEndDate(nextFlight);
  const startTime = getFlightStartTime(nextFlight);
  const explicitEndTime = getFlightEndTime(nextFlight);

  const startDateTime = buildDateTime(startDate, startTime);
  let endDateTime = null;

  if (startDateTime && explicitEndDate && explicitEndTime) {
    endDateTime = buildDateTime(explicitEndDate, explicitEndTime);
  }

  if (!endDateTime && startDateTime) {
    const durationMinutes = Number(nextFlight?.duration);
    const hasValidDuration = Number.isFinite(durationMinutes) && durationMinutes > 0;

    if (hasValidDuration) {
      endDateTime = new Date(startDateTime.getTime() + durationMinutes * 60 * 1000);
    }
  }

  if (startDateTime && !endDateTime) {
    endDateTime = new Date(startDateTime.getTime() + 2 * 60 * 60 * 1000);
  }

  const endDate = explicitEndDate || getDatePart(endDateTime) || startDate;
  const isMultiDay = isDifferentCalendarDay(startDate, endDate, startDateTime, endDateTime);

  return { startDateTime, endDateTime, isMultiDay };
}

function getFlightStartDate(nextFlight) {
  return getFlightValue(nextFlight, ['date', 'start_date', 'startDate']);
}

function getFlightEndDate(nextFlight) {
  return getFlightValue(nextFlight, ['end_date', 'endDate', 'return_date', 'returnDate']);
}

function getFlightStartTime(nextFlight) {
  return getFlightValue(nextFlight, ['time', 'start_time', 'startTime', 'booking_time']);
}

function getFlightEndTime(nextFlight) {
  return getFlightValue(nextFlight, ['end_time', 'endTime', 'finish_time', 'finishTime', 'return_time', 'returnTime']);
}

function getReturnBadgeDate(nextFlight, endDateTime) {
  const endDate = getFlightEndDate(nextFlight);

  if (endDate) return formatBadgeDate(endDate);
  if (endDateTime) return formatBadgeDate(endDateTime);

  return '';
}

function formatBadgeDate(dateLike) {
  const date = new Date(dateLike);

  if (Number.isNaN(date.getTime())) return '';

  return date
    .toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
    })
    .toUpperCase();
}

function isDifferentCalendarDay(startDate, endDate, startDateTime, endDateTime) {
  if (startDateTime && endDateTime) {
    return (
      startDateTime.getFullYear() !== endDateTime.getFullYear() ||
      startDateTime.getMonth() !== endDateTime.getMonth() ||
      startDateTime.getDate() !== endDateTime.getDate()
    );
  }

  const parsedStartDate = parseDateOnly(startDate);
  const parsedEndDate = parseDateOnly(endDate);

  if (parsedStartDate && parsedEndDate) {
    return (
      parsedStartDate.getFullYear() !== parsedEndDate.getFullYear() ||
      parsedStartDate.getMonth() !== parsedEndDate.getMonth() ||
      parsedStartDate.getDate() !== parsedEndDate.getDate()
    );
  }

  return false;
}

function getDatePart(dateValue) {
  if (!dateValue || Number.isNaN(dateValue.getTime?.())) return '';

  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, '0');
  const day = String(dateValue.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function getTimePart(dateValue) {
  if (!dateValue || Number.isNaN(dateValue.getTime?.())) return '';

  const hours = String(dateValue.getHours()).padStart(2, '0');
  const minutes = String(dateValue.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
}

function parseDateOnly(dateValue) {
  if (!dateValue) return null;

  const date = new Date(`${String(dateValue).slice(0, 10)}T00:00`);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

function getFlightValue(source, keys) {
  for (const key of keys) {
    if (source?.[key]) return source[key];
  }

  return '';
}

function buildDateTime(date, time) {
  if (!date || !time) return null;

  const timePart = String(time).slice(0, 5);
  const dateTime = new Date(`${date}T${timePart}`);

  if (Number.isNaN(dateTime.getTime())) return null;

  return dateTime;
}

function getStyles(isMobile) {
  const ds = wingitDesign;

  return {
    card: {
      ...ds.cards.primary,
      gridColumn: '1 / -1',
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '320px 1fr 220px',
      gap: isMobile ? '1rem' : '1.25rem',
      alignItems: 'stretch',
      padding: isMobile ? '0.9rem' : '0.85rem',
      borderRadius: isMobile ? ds.radius.large : ds.radius.xl,
      overflow: 'hidden',
    },

    photoPanel: {
      position: 'relative',
      minHeight: isMobile ? 170 : 200,
      borderRadius: ds.radius.large,
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #19304c, #07111f)',
      border: `1px solid ${ds.colors.border}`,
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
    },

    aircraftImage: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },

    imageShade: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(90deg, rgba(5,10,18,0.08), rgba(5,10,18,0.42))',
      zIndex: 1,
    },

    imageOverlay: {
      position: 'absolute',
      inset: 0,
      backgroundImage: `
        linear-gradient(rgba(85,199,255,0.055) 1px, transparent 1px),
        linear-gradient(90deg, rgba(85,199,255,0.045) 1px, transparent 1px)
      `,
      backgroundSize: '34px 34px',
      opacity: 0.45,
    },

    imageIcon: {
      position: 'absolute',
      right: isMobile ? 18 : 24,
      top: isMobile ? 18 : 22,
      color: 'rgba(201,241,255,0.75)',
      filter: 'drop-shadow(0 12px 22px rgba(0,0,0,0.36))',
    },

    photoCaption: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      padding: isMobile ? '1rem' : '1.1rem',
      background: 'linear-gradient(0deg, rgba(5,10,18,0.96), rgba(5,10,18,0.15))',
      zIndex: 2,
    },

    imageLabel: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 950,
      marginBottom: '0.35rem',
    },

    photoRegistration: {
      color: ds.colors.text,
      fontSize: isMobile ? '1.7rem' : '2rem',
      fontWeight: 950,
      letterSpacing: '-0.04em',
      lineHeight: 1,
      textShadow: '0 8px 22px rgba(0,0,0,0.55)',
    },

    imageType: {
      color: ds.colors.textMuted,
      fontSize: '0.86rem',
      fontWeight: 850,
      marginTop: '0.45rem',
    },

    imageDate: {
      color: ds.colors.textSoft,
      fontSize: '0.78rem',
      fontWeight: 850,
      marginTop: '0.55rem',
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
    },

    main: {
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      padding: isMobile ? 0 : '0.45rem 0',
      maxWidth: 520,
    },

    eyebrow: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 950,
      marginBottom: '0.45rem',
    },

    title: {
      margin: 0,
      color: ds.colors.text,
      fontSize: isMobile ? '1.35rem' : '1.75rem',
      fontWeight: 900,
      letterSpacing: '-0.045em',
      lineHeight: 1,
    },

    metaLine: {
      marginTop: '0.8rem',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.55rem',
      color: ds.colors.textSoft,
      fontSize: isMobile ? '0.9rem' : '0.98rem',
      fontWeight: 800,
      flexWrap: 'wrap',
    },

    metaDot: {
      color: ds.colors.primary,
      opacity: 0.85,
    },

    routeBlock: {
      marginTop: '0.7rem',
      width: '100%',
      maxWidth: 360,
      padding: isMobile ? '0.65rem' : '0.75rem',
      borderRadius: ds.radius.large,
      background: 'rgba(255,255,255,0.035)',
      border: `1px solid ${ds.colors.border}`,
      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
    },

    routeSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },

    routeLabel: {
      ...ds.typography.caption,
      color: ds.colors.primary,
      fontWeight: 950,
      marginBottom: '0.55rem',
    },

    routeDirection: {
      color: ds.colors.text,
      fontSize: isMobile ? '1.02rem' : '1.1rem',
      fontWeight: 900,
      letterSpacing: '-0.015em',
      lineHeight: 1.15,
      textTransform: 'uppercase',
    },

    routeAirfield: {
      color: ds.colors.text,
      fontSize: isMobile ? '1.08rem' : '1.22rem',
      fontWeight: 950,
      letterSpacing: '-0.02em',
      lineHeight: 1.15,
      textTransform: 'uppercase',
    },

    routeArrow: {
      color: ds.colors.primary,
      fontSize: '1.2rem',
      fontWeight: 950,
      lineHeight: 1,
      margin: '0.32rem 0',
    },

    purposeChip: {
      ...ds.typography.caption,
      marginTop: '0.55rem',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: ds.colors.text,
      background: 'rgba(255,255,255,0.055)',
      border: `1px solid ${ds.colors.border}`,
      borderRadius: 999,
      padding: '0.35rem 0.7rem',
      fontWeight: 950,
      letterSpacing: '0.08em',
    },
    continuityWarning: {
      width: '100%',
      maxWidth: 360,
      marginTop: '0.65rem',
      padding: '0.65rem 0.75rem',
      borderRadius: ds.radius.medium,
      border: '1px solid rgba(242,184,75,0.42)',
      background: 'rgba(242,184,75,0.09)',
      boxSizing: 'border-box',
      textAlign: 'left',
    },

    continuityWarningTitle: {
      color: '#f2b84b',
      fontSize: '0.76rem',
      fontWeight: 900,
    },

    continuityWarningText: {
      color: ds.colors.textSoft,
      fontSize: '0.78rem',
      fontWeight: 650,
      lineHeight: 1.4,
      marginTop: '0.25rem',
    },

    emptyLine: {
      color: ds.colors.textMuted,
      fontSize: '0.96rem',
      fontWeight: 650,
      lineHeight: 1.45,
      marginTop: '0.9rem',
    },

    statusPanel: {
      borderLeft: isMobile ? 'none' : `1px solid ${ds.colors.border}`,
      paddingLeft: isMobile ? 0 : '0.9rem',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: isMobile ? 'flex-start' : 'center',
      textAlign: isMobile ? 'left' : 'center',
      gap: '0.45rem',
    },

    statusIconWrap: {
      width: 50,
      height: 50,
      borderRadius: 18,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: ds.colors.primary,
      background: 'rgba(85,199,255,0.1)',
      border: `1px solid ${ds.colors.border}`,
    },

    statusBadge: {
      ...ds.typography.caption,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: ds.colors.text,
      background: 'rgba(255,255,255,0.06)',
      border: `1px solid ${ds.colors.border}`,
      borderRadius: 999,
      padding: '0.32rem 0.65rem',
      fontWeight: 950,
      letterSpacing: '0.08em',
    },

    statusText: {
      color: ds.colors.textMuted,
      fontSize: '0.88rem',
      fontWeight: 650,
      lineHeight: 1.45,
      maxWidth: 180,
    },
  };
}

function isEndedFlight(nextFlight) {
  const { startDateTime, endDateTime } = getFlightDateRange(nextFlight);
  const postFlightCompleted =
    String(nextFlight?.tech_log_status || '')
      .trim()
      .toLowerCase() === 'completed';
  const now = new Date();

  if (startDateTime && endDateTime && now > endDateTime && postFlightCompleted) {
    return true;
  }

  return false;
}